import { Box, BoxProps, Grid, GridProps } from '@chakra-ui/react'
import { Children } from 'react'

import { HeightSynchronizer } from '../../widgets/DetailWidget/height-synchronizer'
import {
  type BentoColumnLayout,
  BentoLayout,
  type BentoRowLayout,
} from './bentoLayout'

/** No border on the box — the line is drawn with ::after centered in the grid gap. */
const defaultSeparatorBoxProps: BoxProps = {
  position: 'relative',
  borderColor:
    'var(--bento-separator-color, var(--chakra-colors-presets-neutral-20))',
}

const mergeSeparatorStyle = (separatorStyle?: BoxProps): BoxProps => {
  if (!separatorStyle) {
    return { ...defaultSeparatorBoxProps }
  }
  const { sx: userSx, ...rest } = separatorStyle
  return {
    ...defaultSeparatorBoxProps,
    ...rest,
    ...(userSx !== undefined ? { sx: userSx } : {}),
  }
}

export type BentoRowProps = GridProps & {
  layout?: BentoRowLayout
  /**
   * When true, draw vertical separators between cells (see `separatorStyle`).
   */
  separator?: boolean
  /**
   * Chakra `Box` props merged onto each separator cell (after defaults).
   * Only applies when `separator` is true.
   */
  separatorStyle?: BoxProps
  gap?: string
  /**
   * Maximum columns allowed
   *
   * @default 4
   */
  maxColumns?: number
  /**
   * Minimum columns allowed
   *
   * @default 1
   */
  minColumns?: number
}

export const BentoRow = ({
  gap = '16px',
  layout,
  maxColumns = 4,
  minColumns = 1,
  separator = false,
  separatorStyle,
  ...props
}: BentoRowProps) => {
  const {
    gap: resolvedGap,
    maxColumns: resolvedMaxColumns,
    gridProps,
  } = BentoLayout.resolveRow({ layout, gap, maxColumns })
  const maxColPercent = 100 / resolvedMaxColumns

  const itemsPerRow =
    Children.count(props.children) > resolvedMaxColumns
      ? resolvedMaxColumns
      : Children.count(props.children)

  const separatorBoxProps = separator
    ? mergeSeparatorStyle(separatorStyle)
    : null

  const {
    sx: separatorSx,
    borderColor: separatorLineColor = 'presets.neutral.20',
    ...separatorRest
  } = separatorBoxProps ?? {}

  return (
    <HeightSynchronizer>
      <Grid
        gridTemplateColumns={`repeat(auto-fit, minmax(calc(${maxColPercent}% - ${resolvedGap}), 1fr))`}
        gap={resolvedGap}
        {...props}
        {...gridProps}
      >
        {separatorBoxProps
          ? Children.toArray(props.children).map((child, index) => (
              <Box
                key={(child as any).key || index}
                {...separatorRest}
                sx={{
                  '&::after': {
                    content: '""',
                    position: 'absolute',
                    top: 0,
                    bottom: 0,
                    width: '1px',
                    // Center the 1px line in the grid gutter (border was flush to the cell edge).
                    right: `calc(-0.5 * ${resolvedGap} - 0.5px)`,
                    bg: separatorLineColor,
                  },
                  ...(separatorSx &&
                  typeof separatorSx === 'object' &&
                  !Array.isArray(separatorSx)
                    ? separatorSx
                    : {}),
                  [`&:nth-of-type(${itemsPerRow}n)::after`]: {
                    display: 'none',
                  },
                }}
              >
                {child}
              </Box>
            ))
          : props.children}
      </Grid>
    </HeightSynchronizer>
  )
}

export type BentoColumnProps = BentoRowProps & {
  columnLayout?: BentoColumnLayout
  /**
   * How many columns should this column span?
   *
   * @default 1
   */
  columnSpan?: number
}

export const BentoColumn = ({
  columnSpan = 1,
  columnLayout,
  ...props
}: BentoColumnProps) => (
  <BentoRow
    {...props}
    {...BentoLayout.column(columnLayout)}
    position="relative"
    gridColumn={`span ${columnSpan}`}
    maxColumns={1}
  />
)
